import {
  createIntegration,
  deleteIntegration,
  getIntegrations,
  updateIntegration,
  getIntegration,
  getConnectGoogleUrl,
} from '@app/services/integrations';
import {availableIntegrations, availableIntegrationsReviews, IntegrationType} from '@app/utils/consts';
import { getIntegrationIcon } from '@app/utils/utils';
import {
  createIntegrationValidation,
  updateIntegrationValidation,
} from '@app/utils/validations';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
// import { loginWithGoogle } from '@app/services/auth';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {kebabCase} from "lodash";

export interface IntegrationReviewItem {
  type: IntegrationType;
  title: string;
  Icon: React.FunctionComponent<
    React.SVGProps<SVGSVGElement> & {
      title?: string;
    }
  > | null;
  available: boolean;
  email?: string | null;
  hasConnection: boolean;
  connectionId?: string;
}

export const useIntegrationsReviews = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [integrationFormModal, setIntegrationFormModal] = useState<string | undefined>();
  const [updateIntegrationFormModal, setUpdateIntegrationFormModal] = useState<string | undefined>();
  const [isConnectingGoogle, setIsConnectingGoogle] = useState(false);
  const [confirmDeleteType, setConfirmDeleteType] = useState<IntegrationType | undefined>();
  const oauthMessageDisplayed = useRef(false);

  // Display error from backend when it redirects after google OAuth
  useEffect(() => {

    if (oauthMessageDisplayed.current) return;    // It can be called several times if conflicts with AnimationLayout and businessId removal

    const errorText = searchParams.get('error');
    const successText = searchParams.get('success');

    if (errorText || successText) {
      oauthMessageDisplayed.current = true;
      if (errorText) toast.error(decodeURIComponent(errorText), { autoClose: false, toastId: `oauth-error-${kebabCase(errorText ?? '')}` });            // Assign unique ID to prevent duplicate toasts of the same error message
      if (successText) toast.success(decodeURIComponent(successText), { autoClose: 8000, toastId: `oauth-success-${kebabCase(successText ?? '')}` });   // Assign unique ID to prevent duplicate toasts of the same message

      const newParams = new URLSearchParams(searchParams);
      newParams.delete('error');
      newParams.delete('success');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);


  const saveIntegrationForm = useForm({
    resolver: createIntegrationValidation,
  });
  const updateIntegrationForm = useForm({
    resolver: updateIntegrationValidation,
  });

  // Always declare the query, but control its execution with enabled
  const { data: currentIntegrationData } = useQuery({
    queryKey: ['get-integration', updateIntegrationFormModal],
    queryFn: () => getIntegration(updateIntegrationFormModal || ''),
    enabled: Boolean(updateIntegrationFormModal),
    // Don't cache the result since we want fresh data each time
    gcTime: 0,
    // Don't retry on error to avoid unnecessary requests
    retry: false,
  });

  const connectGoogle = async () => {
    setIsConnectingGoogle(true);
    try {
      const urlResponse = await getConnectGoogleUrl();

      setIsConnectingGoogle(false); // set before redirect

      if (urlResponse?.data?.redirectUrl) {
        window.location.href = urlResponse.data.redirectUrl;
      }
    } finally {
      setIsConnectingGoogle(false);
    }
  };

  const handleIntegrationModal = (type?: string) => {
    setIntegrationFormModal(type);
    if (!type) {
      saveIntegrationForm.reset();
    } else {
      // Reset form when opening modal for a new integration
      saveIntegrationForm.reset();
      // Set the type in the form after reset
      saveIntegrationForm.setValue('type', type as IntegrationType);
    }
  };

  const handleUpdateIntegrationModal = (integration?: IntegrationReviewItem) => {
    // For Google integration, check if user has access token
    if (integration?.type === IntegrationType.GOOGLE) {
      if (!integration.available) {
        // User needs to re-authenticate with Google
        // loginWithGoogle();
        connectGoogle();
        return;
      }
    }
    setUpdateIntegrationFormModal(integration?.type);
    updateIntegrationForm.reset();
  };

  // Effect to handle form updates when integration data is loaded
  useEffect(() => {
    // Try all common paths for connectionId within .data
    let connectionId = undefined;
    const data = currentIntegrationData?.data;
    if (data) {
      connectionId = data.connectionId || data.data?.connectionId || (typeof data.integration === 'string' ? data.integration : data.integration?.connectionId);
    }
    if (connectionId && updateIntegrationFormModal) {
      updateIntegrationForm.setValue('connectionId', connectionId, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [currentIntegrationData, updateIntegrationForm, updateIntegrationFormModal]);

  const {
    data: integrationsResponse,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['get-integrations'],
    queryFn: () => getIntegrations(),
  });

  const saveIntegrationSubmit = (data: { type: string; connectionId?: string }) => {
    // For Google integration, validate URL format
    if (data.type === IntegrationType.GOOGLE && data.connectionId) {
      try {
        new URL(data.connectionId);
      } catch {
        toast.error('Please enter a valid URL for your Google review request link');
        return;
      }
    }

    // For Yelp integration, validate business ID format
    if (data.type === IntegrationType.YELP && data.connectionId) {
      const yelpIdRegex = /^[a-zA-Z0-9-]+$/;
      if (!yelpIdRegex.test(data.connectionId)) {
        toast.error('Invalid Yelp Business ID format');
        return;
      }
    }

    const integrationData = {
      type: data.type as IntegrationType,
      connectionId: data.connectionId,
    };

    createIntegration(integrationData)
      .then(() => {
        setIntegrationFormModal(undefined);
        saveIntegrationForm.reset();
        refetch();
        toast.success('Integration created');
      })
      .catch((err) => {
        console.error(err);
        toast.error('Error creating integration');
      });
  };

  const deleteIntegrationAction = (type: IntegrationType) => {
    setConfirmDeleteType(type);
  };

  const confirmDeleteIntegration = () => {
    if (!confirmDeleteType) return;

    const integrationInfo = integrationsResponse?.data?.integrations?.find((conn: { type: string }) => conn.type === confirmDeleteType);
    deleteIntegration(integrationInfo.id)
      .then(() => {
        refetch();
        toast.success('Integration removed');
      })
      .catch((err) => {
        console.error(err);
        toast.error('Error while removing integration');
      })
      .finally(() => {
        setConfirmDeleteType(undefined);
      });
  };

  const updateIntegrationSubmit = (data: { type: string; connectionId?: string }) => {
    const integrationInfo = integrationsResponse?.data?.integrations?.find((conn: { type: string }) => conn.type === data.type);

    // Validate Yelp business ID format if it's a Yelp integration
    if (data.type === IntegrationType.YELP && data.connectionId) {
      // Yelp business IDs are typically alphanumeric and may contain hyphens
      const yelpIdRegex = /^[a-zA-Z0-9-]+$/;
      if (!yelpIdRegex.test(data.connectionId)) {
        toast.error('Invalid Yelp Business ID format');
        return;
      }
    }

    // Validate Google URL format if it's a Google integration
    if (data.type === IntegrationType.GOOGLE && data.connectionId) {
      try {
        new URL(data.connectionId);
      } catch {
        toast.error('Please enter a valid URL for your Google review request link');
        return;
      }
    }

    const updateData = { connectionId: data.connectionId };

    updateIntegration(integrationInfo.id, updateData)
      .then(() => {
        setUpdateIntegrationFormModal(undefined);
        updateIntegrationForm.reset();
        refetch();
        toast.success('Integration updated successfully');
      })
      .catch((err) => {
        console.error(err);
        toast.error('Error updating integration');
      });
  };

  const handleGoogleIntegration = (integration?: IntegrationReviewItem) => {
    // const googleAccessToken = localStorage.getItem('googleAccessToken');

    if (integration?.type === IntegrationType.GOOGLE && !integration?.available) {
      // User needs to authenticate with Google first
      // loginWithGoogle();
      connectGoogle();
      return;
    }

    // User is authenticated, open the integration modal to create Google integration
    handleIntegrationModal(IntegrationType.GOOGLE);
  };

  const listOfIntegrations = useMemo<IntegrationReviewItem[]>((): IntegrationReviewItem[] => {
    return Array.from(Object.values(IntegrationType))
      .filter((integration) => availableIntegrationsReviews.includes(integration))
      .map((integration) => {
        const Icon = getIntegrationIcon(integration);

        const integrationInfo = integrationsResponse?.data?.integrations?.find((conn: { type: string }) => conn.type === integration);
        const hasConnection = Boolean(integrationInfo);
        // Only disable if the integration is not in availableIntegrations
        const available = availableIntegrations.includes(integration);
        // Get the connectionId from the response if it exists
        const connectionId = integrationInfo?.connectionId;

        // For Google integration, check if user has Google access token
        // const googleAccessToken = localStorage.getItem('googleAccessToken');
        // const isGoogleAvailable = integration === IntegrationType.GOOGLE ? Boolean(googleAccessToken) : available;
        const isGoogleAvailable = integration === IntegrationType.GOOGLE ? Boolean(integrationInfo?.googleToken) && !integrationInfo?.googleToken?.isRevoked : available;

        return {
          type: integration,
          title: integration?.replace('_', ' '),
          Icon,
          available: isGoogleAvailable,
          email: isGoogleAvailable ? integrationInfo?.googleToken?.googleEmail : null,
          hasConnection,
          connectionId,
        };
      });
  }, [integrationsResponse, availableIntegrations]);

  return {
    saveIntegrationForm,
    saveIntegrationSubmit,
    handleIntegrationModal,
    integrationFormModal,
    integrationsResponse,
    error,
    isLoading,
    deleteIntegrationAction,
    confirmDeleteIntegration,
    confirmDeleteType,
    setConfirmDeleteType,
    listOfIntegrations,
    updateIntegrationFormModal,
    handleUpdateIntegrationModal,
    updateIntegrationForm,
    updateIntegrationSubmit,
    currentIntegrationData,
    handleGoogleIntegration,
    isConnectingGoogle,
  };
};
